import { useState, useRef, useEffect } from 'react';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';

export const useDocumentAudioPlayer = () => {
  const [fileId, setFileId] = useState<number | null>(null);
  const [fileName, setFileName] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cleanupListenersRef = useRef<(() => void) | null>(null);
  const { createLink } = useFileTemporaryLinkMutation();

  const stopAndCleanup = () => {
    cleanupListenersRef.current?.();
    cleanupListenersRef.current = null;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
  };

  useEffect(() => stopAndCleanup, []);

  const play = async (newFileId: number, newFileName: string) => {
    stopAndCleanup();
    setFileId(newFileId);
    setFileName(newFileName);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    try {
      const link = await createLink(newFileId);
      const audio = new Audio(link.url);

      const onLoadedMetadata = () => setDuration(audio.duration);
      const onTimeUpdate = () => setCurrentTime(audio.currentTime);
      const onEnded = () => setIsPlaying(false);
      const onError = () => {
        notifyAndLogError(
          'Failed to load audio',
          'The audio file could not be loaded. Please try again later.',
          new Error('Audio element error'),
          { fileId: newFileId },
        );
        setIsPlaying(false);
        setFileId(null);
      };

      audio.addEventListener('loadedmetadata', onLoadedMetadata);
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);

      cleanupListenersRef.current = () => {
        audio.removeEventListener('loadedmetadata', onLoadedMetadata);
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };

      audioRef.current = audio;

      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      notifyAndLogError(
        'Failed to play audio',
        'An error occurred while trying to play the audio file. Please try again later.',
        error,
        { fileId: newFileId },
      );
      setFileId(null);
    }
  };

  const download = async (uploadedFile: UploadedFile, clientId?: string) => {
    try {
      const link = await createLink(uploadedFile.file.id);
      const response = await fetch(link.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = uploadedFile.file.fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(error => {
        notifyAndLogError('Playback failed', 'Unable to play audio. Please try again.', error, {
          fileId,
        });
      });
      setIsPlaying(true);
    }
  };

  const handleSeek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleMuteToggle = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !audioRef.current.muted;
    setIsMuted(prev => !prev);
  };

  const handleClose = () => {
    stopAndCleanup();
    setFileId(null);
    setFileName('');
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  };

  return {
    fileId,
    fileName,
    isVisible: fileId !== null,
    isPlaying,
    currentTime,
    duration,
    isMuted,
    play,
    download,
    handlePlayPause,
    handleSeek,
    handleMuteToggle,
    handleClose,
  };
};
